import { useState, useEffect, useRef } from 'react'
import {
  reportingApiClient,
  type VoicePipelineSessionsListResponse,
} from '@/lib/api/reporting.client'
import { VOICE_PIPELINE_DEFAULT_PAGE_SIZE } from '@super/shared-types'

interface UseVoicePipelineSessionsOptions {
  dateRange: string
  appliedCustomDates: { start: string; end: string } | null
  companyId?: string
  language?: string
  enabled: boolean
}

function computeDateParams(
  dateRange: string,
  appliedCustomDates: { start: string; end: string } | null,
): { startDate: string; endDate: string } | null {
  if (dateRange === 'custom' && appliedCustomDates) {
    return {
      startDate: new Date(appliedCustomDates.start).toISOString(),
      endDate: new Date(appliedCustomDates.end).toISOString(),
    }
  }
  const end = new Date()
  const start = new Date()
  switch (dateRange) {
    case '48h':
      start.setHours(end.getHours() - 48)
      break
    case '7d':
      start.setDate(end.getDate() - 7)
      break
    case '30d':
      start.setDate(end.getDate() - 30)
      break
    case '90d':
      start.setDate(end.getDate() - 90)
      break
    default:
      return null
  }
  return { startDate: start.toISOString(), endDate: end.toISOString() }
}

export function useVoicePipelineSessions(
  options: UseVoicePipelineSessionsOptions,
) {
  const { dateRange, appliedCustomDates, companyId, language, enabled } =
    options

  const [sessionsData, setSessionsData] =
    useState<VoicePipelineSessionsListResponse | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [sortBy, setSortBy] = useState('createdAt')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const lastFetchKeyRef = useRef<string | null>(null)

  // Reset page when filters change
  useEffect(() => {
    setPage(1)
    lastFetchKeyRef.current = null
  }, [
    dateRange,
    appliedCustomDates?.start,
    appliedCustomDates?.end,
    companyId,
    language,
  ])

  useEffect(() => {
    if (!enabled) return

    const dates = computeDateParams(dateRange, appliedCustomDates)
    if (!dates) return

    const fetchKey = `${companyId ?? 'all'}:${language ?? 'all'}:${dates.startDate}:${dates.endDate}:${page}:${sortBy}:${sortDir}`
    if (lastFetchKeyRef.current === fetchKey) return

    let cancelled = false
    const fetchSessions = async () => {
      setIsFetching(true)
      setError(null)
      try {
        const data = await reportingApiClient.getVoicePipelineSessions({
          ...dates,
          companyId,
          language,
          page,
          pageSize: VOICE_PIPELINE_DEFAULT_PAGE_SIZE,
          sortBy,
          sortDir,
        })
        if (!cancelled) {
          setSessionsData(data)
          lastFetchKeyRef.current = fetchKey
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Failed to load sessions data',
          )
          lastFetchKeyRef.current = null
        }
      } finally {
        if (!cancelled) setIsFetching(false)
      }
    }

    fetchSessions()
    return () => {
      cancelled = true
    }
  }, [
    enabled,
    dateRange,
    appliedCustomDates,
    companyId,
    language,
    page,
    sortBy,
    sortDir,
  ])

  const handleSort = (column: string) => {
    if (column === sortBy) {
      setSortDir(prev => (prev === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortBy(column)
      setSortDir('desc')
    }
    setPage(1)
    lastFetchKeyRef.current = null
  }

  return {
    sessionsData,
    isFetching,
    error,
    page,
    setPage: (p: number) => {
      setPage(p)
      lastFetchKeyRef.current = null
    },
    sortBy,
    sortDir,
    handleSort,
  }
}
